// Custom HTTP client to replace Axios

type RequestConfig = {
  url?: string
  method?: string
  headers?: Record<string, string>
  data?: any
  params?: Record<string, any>
  timeout?: number
  responseType?: string
}

type Response<T = any> = {
  data: T
  status: number
  statusText: string
  headers: Record<string, string>
  config: RequestConfig
}

class ResponseError extends Error {
  constructor(
    message: string,
    public response: Response<any>
  ) {
    super(message)
    this.response = response
  }
}
class XHRClient {
  private defaultConfig: RequestConfig = {}

  constructor(config: RequestConfig = {}) {
    this.defaultConfig = config
  }

  private get xCsrfToken(): string {
    const csrfElement = <HTMLInputElement>(
      document.getElementById("x-csrf-token")
    )
    if (csrfElement && csrfElement.value) return csrfElement.value

    return ""
  }

  async request<T = any>(config: RequestConfig): Promise<Response<T>> {
    let mergedConfig = { ...this.defaultConfig, ...config }

    // Add CSRF token to all requests
    mergedConfig.headers = {
      ...mergedConfig.headers,
      "X-CSRF-TOKEN": this.xCsrfToken
    }

    const { url, method = "GET", headers = {}, data, params } = mergedConfig

    // Build URL with query parameters if needed
    let fullUrl = url || ""
    if (params) {
      const queryString = convertObjectToUrl(params)
      fullUrl += (fullUrl.includes("?") ? "&" : "?") + queryString
    }

    // Prepare fetch options
    const fetchOptions: RequestInit = {
      method,
      headers: headers as HeadersInit,
      credentials: "same-origin"
    }

    // Add body for non-GET requests
    if (method !== "GET" && data) {
      if (data instanceof FormData) {
        fetchOptions.body = data
      } else {
        fetchOptions.headers["Content-Type"] = "application/json"
        fetchOptions.body = JSON.stringify(data)
      }
    }

    try {
      const response = await fetch(fullUrl, fetchOptions)
      let data: T = {} as T

      const contentType = response.headers.get("Content-Type")
      if (contentType && contentType.includes("application/json")) {
        data = await response.json()
      } else {
        data = (await response.text()) as unknown as T
      }

      // Convert headers to object
      const responseHeaders: Record<string, string> = {}
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value
      })

      // Create response object similar to Axios
      let responseObj: Response<T> = {
        data: data,
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        config: mergedConfig
      }
      if (!response.ok) {
        throw new ResponseError(response.statusText, responseObj)
      }

      return responseObj
    } catch (error) {
      if (error instanceof ResponseError) {
        throw error
      }
      throw error
    }
  }

  // Helper methods for common HTTP methods
  async get<T = any>(
    url: string,
    config: RequestConfig = {}
  ): Promise<Response<T>> {
    return this.request<T>({ ...config, url, method: "GET" })
  }

  async post<T = any>(
    url: string,
    data?: any,
    config: RequestConfig = {}
  ): Promise<Response<T>> {
    return this.request<T>({ ...config, url, method: "POST", data })
  }

  async put<T = any>(
    url: string,
    data?: any,
    config: RequestConfig = {}
  ): Promise<Response<T>> {
    return this.request<T>({ ...config, url, method: "PUT", data })
  }

  async patch<T = any>(
    url: string,
    data?: any,
    config: RequestConfig = {}
  ): Promise<Response<T>> {
    return this.request<T>({ ...config, url, method: "PATCH", data })
  }

  async delete<T = any>(
    url: string,
    config: RequestConfig = {}
  ): Promise<Response<T>> {
    return this.request<T>({ ...config, url, method: "DELETE" })
  }
}

// Create instance with same API as axios.create()
export const xhr = new XHRClient()

// Maintain the same API for get function
export async function get(url: string): Promise<any> {
  const response = await xhr.get(url)
  return response.data
}

export function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Converts a js object into a url param can convert really nested js
 * objects
 * https://gist.github.com/boriscy/0322df7a1cef79977f20756e7ea7247e
 * @param params {object}
 * @param keys {array}
 * @param isArray {boolean}
 * @returns
 */
export function convertObjectToUrl(
  params: Record<string, any>,
  keys: string[] = [],
  isArray: boolean = false
): string {
  const p = Object.keys(params)
    .map((key) => {
      let val = params[key]

      if (
        "[object Object]" === Object.prototype.toString.call(val) ||
        Array.isArray(val)
      ) {
        if (Array.isArray(params)) {
          keys.push("")
        } else {
          keys.push(key)
        }
        return convertObjectToUrl(val, keys, Array.isArray(val))
      } else {
        let tKey = key

        if (keys.length > 0) {
          const tKeys = isArray ? keys : [...keys, key]
          tKey = tKeys.reduce((str, k) => {
            return "" === str ? k : `${str}[${k}]`
          }, "")
        }
        if (isArray) {
          return `${tKey}[]=${encodeURIComponent(val)}`
        } else {
          return `${tKey}=${encodeURIComponent(val)}`
        }
      }
    })
    .filter((v) => v !== "")
    .join("&")

  keys.pop()
  return p
}

/**
 * Converts and object to an URI string
 * @param obj Object
 * @returns string
 */
export function queryToUrl(params, keys = [], isArray = false) {
  const p = Object.keys(params)
    .map((key) => {
      let val = params[key]

      if (
        "[object Object]" === Object.prototype.toString.call(val) ||
        Array.isArray(val)
      ) {
        if (Array.isArray(params)) {
          keys.push("")
        } else {
          keys.push(key)
        }
        return queryToUrl(val, keys, Array.isArray(val))
      } else {
        let tKey = key

        if (keys.length > 0) {
          const tKeys = isArray ? keys : [...keys, key]
          tKey = tKeys.reduce((str, k) => {
            return "" === str ? k : `${str}[${k}]`
          }, "")
        }
        if (isArray) {
          return `${tKey}[]=${val}`
        } else {
          return `${tKey}=${val}`
        }
      }
    })
    .join("&")

  keys.pop()
  return p
}

/**
 *
 * @param promise Great way to handle errors in promises instead of try catch
 * @returns T
 */
export async function catchError<T>(
  promise: Promise<Response<T>>
): Promise<[ResponseError | null, Response<T> | null]> {
  try {
    const resp = await promise

    return [null, resp]
  } catch (err: any) {
    return [err, null]
  }
}

export function getXHRToken(): string | void {
  const csrfElement = <HTMLInputElement>document.getElementById("x-csrf-token")
  if (!csrfElement || !csrfElement.value) {
    throw new Error("CSRF token not found")
  }
  return csrfElement.value || ""
}
